import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowClockwise, WarningCircle } from 'shared/icons';
import PageHeader from '../components/layout/PageHeader';
import {
    CoverageNote,
    HealthCell,
    MovementsIndicator,
    RebateIndicator,
    RebateRefreshButton,
} from '../components/payments/HealthCell';
import ReleaseReportImporter from '../components/payments/ReleaseReportImporter';
import Button from '../components/ui/Button';
import ErrorCard from '../components/ui/ErrorCard';
import { DateTimeText, Id } from '../components/ui/Mono';
import StandardTable from '../components/ui/StandardTable';
import { LIST_HEALTH_FILTERS, LIST_HEALTH_LABELS } from '../constants/paymentHealth';
import { REBATE_FILTER_OPTIONS, RebateStyle } from '../constants/paymentRebate';
import {
    ORDER_STATUS_PT,
    PAYMENT_STATUS_PT,
    PAYMENT_TYPE_PT,
    STATUS_DETAIL_PT,
    Traduzir,
} from '../constants/paymentVocabulario';
import { useAuth } from '../hooks/useAuth';
import api from '../services/api';
import { FormatMoney } from '../utils/format';

const STATUS_OPTIONS = [
    '',
    'approved',
    'refunded',
    'rejected',
    'cancelled',
    'in_mediation',
    'charged_back',
    'in_process',
    'pending',
    'authorized',
];

const SELECT_CLASS =
    'rounded-lg border border-mono-300 bg-white px-3 py-2 text-sm dark:border-mono-600 dark:bg-mono-800';

const csvEscape = (v) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
function downloadCsv(rows, columns, fileName) {
    const cols = columns.filter((c) => c.exportable !== false);
    const header = cols.map((c) => csvEscape(c.label)).join(',');
    const lines = rows.map((r) =>
        cols.map((c) => csvEscape(typeof c.getValue === 'function' ? c.getValue(r) : r[c.key])).join(','),
    );
    const csv = [header, ...lines].join('\n');
    // Prefixa o BOM (EF BB BF) pro Excel reconhecer UTF-8 (acentos no header).
    const blob = new Blob([new Uint8Array([0xef, 0xbb, 0xbf]), csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${fileName}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
}

export default function Payments() {
    const navigate = useNavigate();
    const { checkPermission } = useAuth();
    const [rows, setRows] = useState([]);
    const podeAtualizar = checkPermission('payments:update');
    // Botão "Buscar rebate no ML" numa linha: o veredito volta consolidado por
    // pedido, com `by_payment` por pagamento — troca só as linhas desse pedido,
    // sem recarregar a página (e sem mexer no cache, que expira sozinho).
    const aplicarRebateBuscado = useCallback((externalOrderId, res) => {
        const porPagamento = new Map((res?.rebate?.by_payment || []).map((b) => [String(b.payment_id), b]));
        if (porPagamento.size === 0) {
            return;
        }
        setRows((atuais) =>
            atuais.map((row) =>
                String(row.external_order_id) === String(externalOrderId) && porPagamento.has(String(row.id))
                    ? { ...row, rebate: porPagamento.get(String(row.id)) }
                    : row,
            ),
        );
    }, []);
    const [coverage, setCoverage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(30);
    const [total, setTotal] = useState(null);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [healthFilter, setHealthFilter] = useState('');
    const [rebateFilter, setRebateFilter] = useState('');
    // Só vem preenchido quando o filtro de rebate está ativo: diz quantos
    // pagamentos o servidor varreu e se bateu no limite da varredura.
    const [rebateScan, setRebateScan] = useState(null);
    const [sortState, setSortState] = useState({ columnKey: 'date_created', direction: 'desc' });
    const [withMovements, setWithMovements] = useState(false);
    // Cache de resultados: evita refetch quando volta a um filtro já carregado
    const cacheRef = useMemo(() => new Map(), []);
    // Cache do TOTAL, separado do cache das páginas: ele depende só dos filtros,
    // então sobrevive à paginação — é o que evita repagar 2,5s a cada página.
    const totalCacheRef = useMemo(() => new Map(), []);

    const offset = (page - 1) * pageSize;

    // Enquanto a contagem não chega, o total é desconhecido — e a tabela cairia
    // em `rows.length`, travando a paginação na página 1. O palpite abaixo diz o
    // que se SABE: "já vi estas linhas, e se a página veio cheia provavelmente há
    // mais". Habilita avançar sem afirmar um total que ainda não temos; quando a
    // contagem chega, o número real substitui o palpite.
    const totalParaPaginador = total ?? offset + rows.length + (rows.length === pageSize ? 1 : 0);

    // O termo digitado só vira consulta 400 ms depois da última tecla. Quem cola
    // um ID e aperta Enter não quer esperar esse tempo — `submitSearch` aplica
    // na hora e cancela o debounce pendente (senão ele reescreveria o mesmo
    // valor logo depois).
    const buscaDebounceRef = useRef(null);

    useEffect(() => {
        buscaDebounceRef.current = setTimeout(() => {
            setSearch(searchInput.trim());
            setPage(1);
        }, 400);
        return () => clearTimeout(buscaDebounceRef.current);
    }, [searchInput]);

    const submitSearch = useCallback((termo) => {
        clearTimeout(buscaDebounceRef.current);
        setSearch(String(termo ?? '').trim());
        setPage(1);
    }, []);

    // Todo filtro vai pro SERVIDOR. A base tem 159.889 payments — filtrar no
    // cliente só enxergaria a página atual e o total do paginador mentiria.
    // Com cache: evita reprocessamento quando volta a um filtro já carregado.
    const queryParams = useMemo(
        () => ({
            ...(search ? { search } : {}),
            ...(statusFilter ? { status: statusFilter } : {}),
            ...(healthFilter ? { health: healthFilter } : {}),
            ...(rebateFilter ? { rebate_state: rebateFilter } : {}),
            ...(withMovements ? { with_movements: 'true' } : {}),
            sort_by: sortState?.columnKey || 'date_created',
            sort_direction: sortState?.direction || 'desc',
        }),
        [search, statusFilter, healthFilter, rebateFilter, withMovements, sortState],
    );

    // A chave identifica UMA PÁGINA, não um conjunto de filtros.
    //
    // Sem `offset`/`limit` aqui, o early-return do cache servia as linhas da
    // página 1 para qualquer página seguinte do mesmo filtro — e nunca refazia a
    // busca. O `load` até era recriado (tem `offset` nas deps), mas o cache comia
    // a mudança. O sintoma ficava escondido atrás da lentidão: a pessoa culpava a
    // demora e não percebia que os dados repetiam.
    const cacheKey = useMemo(
        () =>
            JSON.stringify({
                search,
                statusFilter,
                healthFilter,
                rebateFilter,
                withMovements,
                sort_by: sortState?.columnKey,
                sort_direction: sortState?.direction,
                offset,
                pageSize,
            }),
        [search, statusFilter, healthFilter, rebateFilter, withMovements, sortState, offset, pageSize],
    );

    // O TOTAL não depende da página — só dos filtros. Guardá-lo à parte é o que
    // permite paginar sem pagar de novo a contagem, que é onde estão os 2,5s.
    const totalKey = useMemo(
        () => JSON.stringify({ search, statusFilter, healthFilter, rebateFilter, withMovements }),
        [search, statusFilter, healthFilter, rebateFilter, withMovements],
    );

    // Uma varredura fria do filtro de rebate leva segundos; trocar o filtro no
    // meio deixava a resposta ANTIGA chegar depois e sobrescrever a página nova.
    // Só a rodada mais recente pode mexer no estado.
    const loadSeqRef = useRef(0);
    const load = useCallback(async () => {
        const rodada = ++loadSeqRef.current;
        const atual = () => rodada === loadSeqRef.current;
        setLoading(true);
        setError(null);
        try {
            const emCache = cacheRef.get(cacheKey);
            if (emCache) {
                setRows(emCache.results);
                setCoverage(emCache.coverage);
                setRebateScan(emCache.rebateScan);
            } else {
                // Página SEM contagem: é o que a torna instantânea. Medido na base
                // local: a página sai em ~100ms; pedir o total no mesmo request
                // fazia ela esperar os 2,5s da contagem sem necessidade.
                const data = await api.getPayments({
                    limit: pageSize,
                    offset,
                    with_total: 'false',
                    ...queryParams,
                });
                const results = data.results || [];
                const scan = data.rebate_scan ?? null;
                cacheRef.set(cacheKey, { results, coverage: data.coverage || null, rebateScan: scan });
                if (!atual()) {
                    return;
                }
                setRows(results);
                setCoverage(data.coverage || null);
                setRebateScan(scan);
                // Com filtro de rebate o servidor já varreu tudo que ia contar
                // para montar a página, então o total vem de graça na mesma
                // resposta — não faz sentido esperar o 2º request só para isso.
                if (scan && typeof data.total === 'number') {
                    totalCacheRef.set(totalKey, data.total);
                    setTotal(data.total);
                }
            }
        } catch (err) {
            if (!atual()) {
                return;
            }
            setError(err.message || 'Erro ao carregar pagamentos');
            // Sem isso a nota "filtro aplicado aos N pagamentos" do filtro anterior
            // ficava órfã em cima de uma lista que falhou.
            setRebateScan(null);
        } finally {
            if (atual()) {
                setLoading(false);
            }
        }
    }, [pageSize, offset, queryParams, cacheKey, cacheRef, totalKey, totalCacheRef]);

    /**
     * A contagem vem DEPOIS e por conta própria, porque ela é o gargalo: o
     * predicado de saúde é uma disjunção de subqueries correlacionadas, e contar
     * exige avaliá-la nas 159.889 linhas enquanto a página para nas primeiras do
     * índice de data.
     *
     * Depende só dos FILTROS, não da página — então paginar dentro do mesmo
     * filtro não paga a contagem de novo. Até ela chegar, `total` é `null` e o
     * rodapé diz "contando…" em vez de exibir um zero que seria mentira.
     */
    useEffect(() => {
        const cacheado = totalCacheRef.get(totalKey);
        if (cacheado !== undefined) {
            setTotal(cacheado);
            return;
        }
        let cancelado = false;
        setTotal(null);
        api.getPayments({ limit: 1, offset: 0, ...queryParams })
            .then((data) => {
                if (cancelado) {
                    return;
                }
                const valor = data.paging?.total ?? 0;
                totalCacheRef.set(totalKey, valor);
                setTotal(valor);
            })
            .catch(() => {
                // Falha na contagem não derruba a lista: as linhas já estão na
                // tela e continuam corretas sem o total.
                if (!cancelado) {
                    setTotal(null);
                }
            });
        return () => {
            cancelado = true;
        };
    }, [totalKey, queryParams, totalCacheRef]);

    useEffect(() => {
        load();
    }, [load]);

    const columns = useMemo(
        // eslint-disable-next-line react-hooks/exhaustive-deps
        () => [
            {
                key: 'health',
                label: 'Saúde',
                sortable: false,
                getValue: (r) => {
                    const h = r.health || {};
                    const rotulo = LIST_HEALTH_LABELS[h.status] || h.status || '';
                    return (h.reasons || []).length ? `${rotulo}: ${h.reasons.map((x) => x.id).join(' ')}` : rotulo;
                },
                render: (r) => <HealthCell health={r.health} />,
            },
            {
                key: 'has_movements',
                label: 'Mov.',
                align: 'center',
                sortable: false,
                getValue: (r) => (r.has_movements ? 1 : 0),
                render: (r) => <MovementsIndicator hasMovements={r.has_movements} result={r.movements_result} />,
            },
            {
                key: 'rebate',
                label: 'Rebate',
                align: 'center',
                sortable: false,
                // Rótulo + valor recebido, para a busca e o CSV acharem
                // tanto "Faltou rebate" quanto "R$ 39,49".
                getValue: (r) => {
                    const rotulo = RebateStyle(r.rebate?.state).label;
                    const total = FormatMoney(r.rebate?.received?.total, { blank: '' });
                    return total ? `${rotulo} · ${total}` : rotulo;
                },
                render: (r) => (
                    <span className="inline-flex items-center gap-1.5">
                        <RebateIndicator rebate={r.rebate} />
                        {podeAtualizar ? (
                            <RebateRefreshButton
                                compact
                                orderId={r.external_order_id}
                                onRefreshed={(res) => aplicarRebateBuscado(r.external_order_id, res)}
                            />
                        ) : null}
                    </span>
                ),
            },
            {
                key: 'id',
                label: 'Payment ID',
                getValue: (r) => r.id,
                render: (r) =>
                    r.payment_pending_sync ? (
                        <span
                            className="text-[11px] text-mono-500"
                            title="O extrato do MP já tem lançamento deste pedido, mas o pagamento ainda não foi buscado na API."
                        >
                            aguardando pagamento
                        </span>
                    ) : (
                        <Id value={r.id} className="text-[#3483fa]" />
                    ),
            },
            {
                key: 'external_order_id',
                label: 'Order ID',
                getValue: (r) => r.external_order_id,
                render: (r) => <Id value={r.external_order_id} className="text-amber-600 dark:text-amber-400" />,
            },
            {
                key: 'status',
                label: 'Status',
                getValue: (r) => r.status,
                render: (r) => {
                    // O código cru vai para o tooltip: quem opera a página lê
                    // português; quem depura precisa do valor original da API.
                    const detalhe = Traduzir(STATUS_DETAIL_PT, r.status_detail);
                    const title = [
                        `Status: ${r.status}`,
                        r.status_detail
                            ? `Detalhe: ${r.status_detail}${detalhe && detalhe !== r.status_detail ? ` (${detalhe})` : ''}`
                            : null,
                    ]
                        .filter(Boolean)
                        .join('\n');
                    return <span title={title}>{Traduzir(PAYMENT_STATUS_PT, r.status) || '—'}</span>;
                },
            },
            {
                key: 'order_status',
                label: 'Status do Pedido',
                sortable: false,
                getValue: (r) => r.order_status || '',
                // O status da ORDER, ao lado do status do PAGAMENTO: é a
                // combinação dos dois que explica metade dos apontamentos
                // (pedido cancelado com pagamento aprovado, por exemplo).
                // Mostrar apenas uma linha; a segunda informação vai para tooltip.
                render: (r) => {
                    const title = r.order_shipping_status
                        ? `Status: ${r.order_status}\nEnvio: ${r.order_shipping_status}`
                        : r.order_status;
                    return r.order_status ? (
                        <span
                            className={r.order_status === 'cancelled' ? 'text-danger-text dark:text-danger-300' : ''}
                            title={title}
                        >
                            {Traduzir(ORDER_STATUS_PT, r.order_status)}
                        </span>
                    ) : (
                        <span className="text-mono-400">—</span>
                    );
                },
            },
            {
                key: 'nf_viva_count',
                label: 'NF-e',
                align: 'center',
                sortable: false,
                headerClassName: 'whitespace-nowrap',
                getValue: (r) => (r.nf_venda_count == null ? '' : r.nf_viva_count),
                // Quatro casos, e a diferença entre os dois últimos importa:
                // "sem NF" é uma CONCLUSÃO (o acervo cobre a data e não achou
                // nota); "aguardando" é falta de dado. Quem decide qual é qual é
                // o backend — aqui só se lê a dimensão FISCAL do veredito.
                // NF-e com hyperlink para abrir o detalhe (onde há acesso ao XML).
                render: (r) => {
                    if (r.nf_venda_count == null) {
                        return (
                            <span className="text-mono-400" title="Acervo de NF-e indisponível">
                                —
                            </span>
                        );
                    }
                    if (r.nf_viva_count > 0) {
                        return (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/payments/order/${r.external_order_id}`);
                                }}
                                className="text-violet-600 hover:underline dark:text-violet-400"
                                title="NF-e de venda autorizada · clique para abrir o detalhe"
                            >
                                {r.nf_viva_count > 1 ? `${r.nf_viva_count} vivas` : 'viva'}
                            </button>
                        );
                    }
                    if (r.nf_venda_count > 0) {
                        return (
                            <span className="text-mono-500" title="NF-e de venda cancelada">
                                cancelada
                            </span>
                        );
                    }
                    const fiscal = r.health?.dimensions?.FISCAL;
                    if (fiscal === 'pending') {
                        return (
                            <span
                                className="text-mono-400"
                                title="A order é mais recente que o acervo de NF-e importado"
                            >
                                aguardando
                            </span>
                        );
                    }
                    if (fiscal === 'unchecked') {
                        return (
                            <span className="text-mono-400" title="Sem captura: não se espera NF-e">
                                —
                            </span>
                        );
                    }
                    return (
                        <span
                            className="text-warning-text dark:text-warning-300"
                            title="O acervo cobre a data deste pedido e não há NF-e de venda vinculada"
                        >
                            sem NF
                        </span>
                    );
                },
            },
            {
                key: 'payment_type_id',
                label: 'Método',
                getValue: (r) => r.payment_type_id,
                // Encurtado e traduzido; o código original fica no tooltip.
                render: (r) =>
                    r.payment_type_id ? (
                        <span title={r.payment_type_id}>{Traduzir(PAYMENT_TYPE_PT, r.payment_type_id)}</span>
                    ) : (
                        <span className="text-mono-400">—</span>
                    ),
            },
            {
                key: 'transaction_amount',
                label: 'Transação',
                align: 'right',
                getValue: (r) => Number(r.transaction_amount || 0),
                render: (r) => FormatMoney(r.transaction_amount),
            },
            {
                key: 'coupon_amount',
                label: 'Cupom',
                align: 'right',
                getValue: (r) => Number(r.coupon_amount || 0),
                render: (r) => (Number(r.coupon_amount || 0) > 0 ? FormatMoney(r.coupon_amount) : '—'),
            },
            {
                key: 'net_received_amount',
                label: 'Líquido',
                align: 'right',
                getValue: (r) => Number(r.net_received_amount || 0),
                render: (r) => (
                    <span className="font-semibold text-green-600 dark:text-green-400">
                        {FormatMoney(r.net_received_amount)}
                    </span>
                ),
            },
            {
                key: 'transaction_amount_refunded',
                label: 'Estornado',
                align: 'right',
                getValue: (r) => Number(r.transaction_amount_refunded || 0),
                render: (r) =>
                    Number(r.transaction_amount_refunded || 0) > 0 ? (
                        <span className="text-red-600 dark:text-red-400">
                            {FormatMoney(r.transaction_amount_refunded)}
                        </span>
                    ) : (
                        '—'
                    ),
            },
            {
                key: 'date_created',
                label: 'Criado',
                getValue: (r) => r.date_created,
                render: (r) => <DateTimeText value={r.date_created} relative />,
            },
        ],
        // `navigate` entra porque o link da NF-e o usa. É estável no react-router,
        // então declarar não recria as colunas a cada render — e evita silenciar o
        // exhaustive-deps, que é o tipo de aviso que esconde bug meses depois.
        [navigate, podeAtualizar, aplicarRebateBuscado],
    );

    // Exporta TODAS as páginas (não só a atual): pagina a API com os mesmos
    // filtros/ordenação e baixa o CSV inteiro.
    const exportAll = useCallback(async () => {
        const pageLimit = 500;
        let all = [];
        let off = 0;
        let totalCount = Infinity;
        while (all.length < totalCount) {
            const data = await api.getPayments({ limit: pageLimit, offset: off, ...queryParams });
            const results = data.results || [];
            all = all.concat(results);
            totalCount = data.paging?.total ?? all.length;
            off += pageLimit;
            if (results.length === 0) {
                break;
            }
        }
        downloadCsv(all, columns, 'pagamentos');
    }, [queryParams, columns]);

    return (
        <div className="space-y-6">
            <PageHeader
                title="Pagamentos"
                subtitle="Tracking de orders e payments — Mercado Livre + MercadoPago"
                actions={
                    <Button onClick={load}>
                        <ArrowClockwise size={18} className="mr-2" />
                        Atualizar
                    </Button>
                }
            />

            {/* Filtros fora do cabeçalho: quatro controles ao lado do título
                espremiam o subtítulo em três linhas. Aqui cada um tem legenda e a
                linha quebra sozinha em tela estreita. */}
            <div className="flex flex-wrap items-end gap-3">
                <label className="flex flex-col gap-1 text-xs font-medium text-mono-500">
                    Saúde
                    <select
                        value={healthFilter}
                        onChange={(e) => {
                            setHealthFilter(e.target.value);
                            setPage(1);
                        }}
                        className={SELECT_CLASS}
                        title="Filtra pelo veredito da linha (aplicado na base inteira, não só nesta página)"
                    >
                        {LIST_HEALTH_FILTERS.map((opt) => (
                            <option key={opt.value || 'all'} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </label>
                <label className="flex flex-col gap-1 text-xs font-medium text-mono-500">
                    Rebate
                    <select
                        value={rebateFilter}
                        onChange={(e) => {
                            setRebateFilter(e.target.value);
                            setPage(1);
                        }}
                        className={SELECT_CLASS}
                        title="Filtra pelo veredito de rebate (varre os pagamentos mais recentes no servidor)"
                    >
                        {REBATE_FILTER_OPTIONS.map((opt) => (
                            <option key={opt.value || 'all'} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </label>
                <label className="flex flex-col gap-1 text-xs font-medium text-mono-500">
                    Status do pagamento
                    <select
                        value={statusFilter}
                        onChange={(e) => {
                            setStatusFilter(e.target.value);
                            setPage(1);
                        }}
                        className={SELECT_CLASS}
                    >
                        {STATUS_OPTIONS.map((s) => (
                            <option key={s || 'all'} value={s}>
                                {s || 'Todos os status'}
                            </option>
                        ))}
                    </select>
                </label>
                <label
                    className={`flex items-center gap-2 ${SELECT_CLASS}`}
                    title="Mostra só pedidos que têm lançamentos do extrato MP importado"
                >
                    <input
                        type="checkbox"
                        checked={withMovements}
                        onChange={(e) => {
                            setWithMovements(e.target.checked);
                            setPage(1);
                        }}
                        className="h-4 w-4"
                    />
                    Só com movimentações
                </label>
            </div>

            {error ? <ErrorCard message={error} /> : null}

            <CoverageNote coverage={coverage} />

            {rebateScan ? (
                // O veredito de rebate não é coluna no banco: o servidor calcula
                // linha a linha e só varre os N mais recentes. Se bateu no limite,
                // o total do paginador é um piso — a pessoa precisa saber disso.
                <p className="flex items-start gap-1.5 text-xs text-mono-500">
                    <WarningCircle size={14} className="mt-0.5 shrink-0" />
                    <span>
                        Filtro de rebate aplicado aos {Number(rebateScan.scanned || 0).toLocaleString('pt-BR')}{' '}
                        pagamentos mais recentes
                        {rebateScan.truncated ? ' — há mais além desse limite; refine por status ou busca.' : '.'}
                    </span>
                </p>
            ) : null}

            <StandardTable
                rows={rows}
                columns={columns}
                // Pedido vindo do extrato ainda pode não ter pagamento; o
                // backend devolve o id do pedido no lugar, para a chave existir.
                rowKey={(r) => r.id ?? r.external_order_id}
                loading={loading}
                emptyMessage={
                    rebateFilter
                        ? 'Nenhum pagamento com esse veredito de rebate entre os mais recentes varridos.'
                        : healthFilter === 'attention'
                          ? 'Nenhum pedido com atenção nos filtros atuais.'
                          : 'Nenhum pagamento. Rode o backfill pra popular.'
                }
                searchPlaceholder="Buscar por payment ID ou order ID"
                onRowClick={(r) => navigate(`/payments/order/${r.external_order_id}`)}
                searchMode="server"
                searchTerm={searchInput}
                onSearchTermChange={setSearchInput}
                onSearchSubmit={submitSearch}
                sortMode="server"
                sortState={sortState}
                onSortStateChange={setSortState}
                paginationMode="server"
                totalItems={totalParaPaginador}
                currentPage={page}
                onPageChange={setPage}
                itemsPerPage={pageSize}
                onItemsPerPageChange={(n) => {
                    setPageSize(n);
                    setPage(1);
                }}
                pageSizeOptions={[15, 30, 50, 100]}
                onExport={exportAll}
            />

            {checkPermission('payments:update') ? <ReleaseReportImporter /> : null}
        </div>
    );
}
